import React, { useCallback, useEffect, useState } from 'react';
import curveIcon from '../../../../AssetsMaps/curve.svg';
import './IntersectionPanelContent.css';

const DRAWING_MODES = [
  { value: null, label: 'Move', icon: 'fa-solid fa-hand-back-fist', title: 'Pan/move map (no drawing)' },
  { value: 'marker', label: 'Point', icon: 'fa-solid fa-crosshairs', title: 'Place a point (marker)' },
  { value: 'polyline', label: 'Line', icon: 'diagonal_line', iconType: 'material-symbol', title: 'Draw a straight line (polyline)' },
  { value: 'polyline-curved', label: 'Curve', icon: curveIcon, iconType: 'image', title: 'Draw a curved line (click 2 points to create editable curve)' },
  { value: 'measure-distance', label: 'Measure', icon: 'fa-ruler', title: 'Measure distance (click points on map)' },
  { value: 'polygon', label: 'Polygon', icon: 'fa-draw-polygon', title: 'Draw a polygon' },
  { value: 'circle', label: 'Circle', icon: 'fa-circle', title: 'Draw a circle' },
  { value: 'rectangle', label: 'Rectangle', icon: 'fa-square', title: 'Draw a rectangle' },
];
function getOverlayDisplayType(overlay) {
  return overlay.type === 'polyline-curved' ? 'Curved line' : overlay.type;
}

/** Format overlay for copy (coordinates or controlPoints) */
function formatOverlayForCopy(overlay) {
  const { id, type, coordinates, controlPoints } = overlay;
  const payload = type === 'polyline-curved' ? { id, type, controlPoints } : { id, type, coordinates };
  return JSON.stringify(payload, null, 2);
}

/**
 * Intersection panel content: drawing tools and list of drawn shapes with coordinates.
 * Used when path-map-left-sidebar "Intersection" button is active.
 */
export default function IntersectionPanelContent({
  drawingMode = null,
  onDrawingModeChange,
  drawnOverlays = [],
  onClearOverlays,
  onOverlayUpdate,
  onOverlayRemove,
  curvedLineEditPointCount = 5,
  onCurvedLineEditPointCountChange,
}) {
  const [copiedId, setCopiedId] = useState(null);
  const isCurvedMode = drawingMode === 'polyline-curved';
  const editPointCount = Math.max(3, Math.min(10, curvedLineEditPointCount));

  useEffect(() => {
    const linkId = 'material-symbols-diagonal-line-font';
    if (document.getElementById(linkId)) return;
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&icon_names=diagonal_line';
    document.head.appendChild(link);
  }, []);

  const handleCopyAll = useCallback(() => {
    const text = JSON.stringify(drawnOverlays.map((o) => (o.type === 'polyline-curved' ? { id: o.id, type: o.type, controlPoints: o.controlPoints } : { id: o.id, type: o.type, coordinates: o.coordinates })), null, 2);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId('all');
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, [drawnOverlays]);

  const handleTypeLabelChange = useCallback((overlayId, label) => {
    onOverlayUpdate?.(overlayId, { label });
  }, [onOverlayUpdate]);

  return (
    <div className="paths-panel-mode-content paths-panel-mode-content-intersection">
      <div className="paths-panel-drawing-tools">
        <span className="paths-panel-drawing-tools-label">Drawing tools</span>
        <div className="paths-panel-drawing-tools-buttons" role="group" aria-label="Drawing tool">
          {DRAWING_MODES.map(({ value, label, icon, iconType, title }) => (
            <button
              key={value ?? 'null'}
              type="button"
              className={`paths-panel-drawing-tool-btn ${drawingMode === value ? 'paths-panel-drawing-tool-btn-active' : ''}`}
              onClick={() => onDrawingModeChange?.(value)}
              title={title}
              aria-pressed={drawingMode === value}
              aria-label={title}
            >
              {iconType === 'material-symbol' ? (
                <span
                  className={`material-symbols-outlined paths-panel-drawing-tool-material-icon ${value === 'polyline' ? 'paths-panel-drawing-tool-line-icon' : ''}`}
                  aria-hidden
                >
                  {icon}
                </span>
              ) : iconType === 'image' ? (
                <span
                  className={`paths-panel-drawing-tool-custom-icon ${value === 'polyline-curved' ? 'paths-panel-drawing-tool-curve-icon' : ''}`}
                  style={{ '--paths-panel-icon-url': `url(${icon})` }}
                  aria-hidden
                />
              ) : (
                <i className={icon.includes(' ') ? `fa ${icon}` : `fas ${icon}`} aria-hidden />
              )}
              <span className="paths-panel-drawing-tool-label">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {isCurvedMode && (
        <div className="paths-panel-curved-line-actions">
          <span className="paths-panel-curved-line-status">
            Click 2 points to create an editable curve
          </span>
        </div>
      )}

      {isCurvedMode && (
        <div className="paths-panel-curved-edit-points">
          <label className="paths-panel-curved-edit-points-label" htmlFor="paths-panel-curved-edit-points-slider">
            Edit points along curve
          </label>
          <div className="paths-panel-curved-edit-points-row">
            <input
              id="paths-panel-curved-edit-points-slider"
              type="range"
              min={3}
              max={10}
              step={1}
              value={editPointCount}
              onChange={(e) => onCurvedLineEditPointCountChange?.(Number(e.target.value))}
              className="paths-panel-curved-edit-points-slider"
              title="Number of draggable points along the curve (3-10)"
            />
            <span className="paths-panel-curved-edit-points-value">{editPointCount}</span>
          </div>
          <p className="paths-panel-curved-edit-points-hint">
            Points are placed at equal distance; drag any point to change the curve.
          </p>
        </div>
      )}

      <div className="paths-panel-drawn-shapes">
        <div className="paths-panel-drawn-shapes-header">
          <span className="paths-panel-drawn-shapes-title">Drawn shapes</span>
          {drawnOverlays.length > 0 && (
            <div className="paths-panel-drawn-shapes-actions">
              <button
                type="button"
                className="paths-panel-drawn-copy-all-btn"
                onClick={handleCopyAll}
                title="Copy all coordinates as JSON"
              >
                {copiedId === 'all' ? <i className="fas fa-check" /> : <i className="fas fa-copy" />}
                {copiedId === 'all' ? ' Copied' : ' Copy all'}
              </button>
              <button
                type="button"
                className="paths-panel-drawn-clear-btn"
                onClick={() => onClearOverlays?.()}
                title="Remove all shapes from the map"
              >
                <i className="fas fa-trash-alt" aria-hidden />
                Clear all
              </button>
            </div>
          )}
        </div>
        {drawnOverlays.length === 0 ? (
          <p>Select a tool above and draw on the map. Coordinates appear here.</p>
        ) : (
          <ul className="paths-panel-drawn-list" aria-label="Drawn shapes with coordinates">
            {drawnOverlays.map((overlay) => (
              <li key={overlay.id} className="paths-panel-drawn-item">
                <div className="paths-panel-drawn-item-header">
                  <input
                    type="text"
                    className="paths-panel-drawn-item-type"
                    value={overlay.label ?? getOverlayDisplayType(overlay)}
                    onChange={(e) => handleTypeLabelChange(overlay.id, e.target.value)}
                    aria-label={`Edit label for ${getOverlayDisplayType(overlay)}`}
                  />
                  <button
                    type="button"
                    className="paths-panel-drawn-item-delete"
                    onClick={() => onOverlayRemove?.(overlay.id)}
                    title="Delete this shape"
                    aria-label={`Delete ${getOverlayDisplayType(overlay)}`}
                  >
                    <i className="fa-solid fa-trash-can" aria-hidden />
                  </button>
                </div>
                <pre className="paths-panel-drawn-item-coords">
                  {overlay.type === 'marker' && overlay.coordinates?.[0] && (
                    <>lat: {overlay.coordinates[0].lat}, lng: {overlay.coordinates[0].lng}</>
                  )}
                  {overlay.type === 'circle' && overlay.coordinates?.[0] && (
                    <>center: {overlay.coordinates[0].lat}, {overlay.coordinates[0].lng}; radius: {overlay.coordinates[0].radiusMeters ?? '?'} m</>
                  )}
                  {(overlay.type === 'polyline' || overlay.type === 'polygon') && overlay.coordinates?.length > 0 && (
                    overlay.coordinates.map((p, i) => (
                      <span key={i}>
                        [{i}] {p.lat}, {p.lng}{i < overlay.coordinates.length - 1 ? '\n' : ''}
                      </span>
                    ))
                  )}
                  {overlay.type === 'polyline-curved' && overlay.controlPoints?.length > 0 && (
                    <>
                      {overlay.controlPoints.length} edit points (drag to change curve)
                      {'\n'}
                      {overlay.controlPoints.map((p, i) => (
                        <span key={i}>
                          [{i}] {p.lat}, {p.lng}{i < overlay.controlPoints.length - 1 ? '\n' : ''}
                        </span>
                      ))}
                    </>
                  )}
                  {overlay.type === 'rectangle' && overlay.coordinates?.length >= 2 && (
                    <>SW: {overlay.coordinates[0].lat}, {overlay.coordinates[0].lng} — NE: {overlay.coordinates[1].lat}, {overlay.coordinates[1].lng}</>
                  )}
                </pre>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


